export function easeInOutCubic(x: number): number {
	return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}

export function generateWavyCircularPath(
	center: number,
	radius: number,
	amplitude: number,
	wavelength: number,
	phase = 0,
): string {
	const circumference = 2 * Math.PI * radius;
	const numWaves = Math.max(
		3,
		Math.round(circumference / Math.max(1, wavelength)),
	);
	const steps = numWaves * 4;
	const dt = (2 * Math.PI) / steps;

	const rAt = (t: number) =>
		radius + amplitude * Math.sin(numWaves * t + phase);
	const drAt = (t: number) =>
		amplitude * numWaves * Math.cos(numWaves * t + phase);
	const xAt = (t: number) => center + rAt(t) * Math.cos(t);
	const yAt = (t: number) => center + rAt(t) * Math.sin(t);
	const dxAt = (t: number) => drAt(t) * Math.cos(t) - rAt(t) * Math.sin(t);
	const dyAt = (t: number) => drAt(t) * Math.sin(t) + rAt(t) * Math.cos(t);

	let d = "";
	const tStart = 0;

	for (let i = 0; i < steps; i++) {
		const t0 = tStart + i * dt;
		const t1 = tStart + (i + 1) * dt;

		const scale = dt / 3;
		const cp1x = xAt(t0) + scale * dxAt(t0);
		const cp1y = yAt(t0) + scale * dyAt(t0);
		const cp2x = xAt(t1) - scale * dxAt(t1);
		const cp2y = yAt(t1) - scale * dyAt(t1);

		if (i === 0) d += `M ${xAt(t0).toFixed(2)} ${yAt(t0).toFixed(2)}`;
		d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${xAt(t1).toFixed(2)} ${yAt(t1).toFixed(2)}`;
	}
	return `${d} Z`;
}

export function getSinePath(
	startX: number,
	endX: number,
	phase: number,
	wl: number,
	amp: number,
): string {
	if (startX >= endX) return "";
	if (amp <= 0.01) {
		return `M ${startX.toFixed(2)} 0 L ${endX.toFixed(2)} 0`;
	}

	const safeWl = Math.max(1, wl);
	const k = (2 * Math.PI) / safeWl;
	const phi = (phase / safeWl) * 2 * Math.PI;

	const yAt = (x: number) => amp * Math.sin(k * x + phi);
	const dyAt = (x: number) => amp * k * Math.cos(k * x + phi);

	const step = safeWl / 4; // 4 segments per wavelength for accurate sine wave
	let d = `M ${startX.toFixed(2)} ${yAt(startX).toFixed(2)}`;

	let currentX = startX;
	while (currentX < endX) {
		const nextX = Math.min(endX, currentX + step);
		const dx = nextX - currentX;
		const scale = dx / 3;

		const y0 = yAt(currentX);
		const dy0 = dyAt(currentX);
		const y1 = yAt(nextX);
		const dy1 = dyAt(nextX);

		const cp1x = currentX + scale;
		const cp1y = y0 + scale * dy0;
		const cp2x = nextX - scale;
		const cp2y = y1 - scale * dy1;

		d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${nextX.toFixed(2)} ${y1.toFixed(2)}`;
		currentX = nextX;
	}

	return d;
}
